// external imports
import * as d3 from 'npm:d3';

// internal imports
import { DeptsData, FileDict, FundsData, OrdinanceData, years } from '@/lib/types.ts';

const width = 640;
const height = 400;

const margin = {
    top: 20,
    right: 20,
    bottom: 30,
    left: 40,
};

const createAnnualTrendsGraph = (ordinanceData: OrdinanceData[], fundsDict: FileDict<FundsData>) => {
    const budgets = years.flatMap((year) => {
        const { local, grants } = ordinanceData.reduce(
            (acc, row) =>
                fundsDict[row.fundCode].type === 'LOCAL'
                    ? { grants: acc.grants, local: acc.local + row[year] }
                    : { grants: acc.grants + row[year], local: acc.local },
            { local: 0, grants: 0 },
        );

        return { year, local, grants };
    });

    const budgetsTidy = years.flatMap((year) => {
        const yearBudget = budgets.find((budgetRow) => budgetRow.year === year);
        return [
            { year, type: 'local', value: yearBudget?.local ?? 0 },
            { year, type: 'grants', value: yearBudget?.grants ?? 0 },
        ];
    });

    // Declare the x (horizontal position) scale.
    const x = d3
        .scaleLinear()
        .domain(d3.extent(years) as [number, number])
        .range([margin.left, width - margin.right]);

    // Declare the y (vertical position) scale.
    const y = d3
        .scaleLinear()
        .domain([0, d3.max(budgets, (d) => d.local + d.grants)] as [
            number,
            number,
        ])
        .range([height - margin.bottom, margin.top]);

    const series = d3
        .stack()
        .keys(['local', 'grants'])
        .value(([, group], key) => group.get(key).value)(d3.index(budgetsTidy, (d) => d.year, (d) => d.type));

    const color = d3
        .scaleOrdinal()
        .domain(series.map((d) => d.key))
        .range(d3.schemeTableau10);

    // Construct an area shape.
    const area = d3
        .area()
        .x((d) => x(d.data[0]))
        .y0((d) => y(d[0]))
        .y1((d) => y(d[1]));

    // Create the SVG annualTrends.
    const annualTrends = d3
        .create('svg')
        .attr('width', width)
        .attr('height', height);

    // Add the x-axis.
    annualTrends
        .append('g')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(x).tickFormat(d3.format('d')));

    // Add the y-axis.
    annualTrends
        .append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(
            d3
                .axisLeft(y)
                .tickFormat((s) => d3.format('.2s')(s).replace('G', 'B')),
        );

    annualTrends
        .append('g')
        .selectAll()
        .data(series)
        .join('path')
        .attr('fill', (d) => color(d.key))
        .attr('d', area)
        .append('title')
        .text((d) => d.key);

    // Append the SVG element.
    return annualTrends.node();
};

const graphAnnualTrends = (
    annualTrends: HTMLElement,
    ordinanceData: OrdinanceData[],
    deptsDict: FileDict<DeptsData>,
    fundsDict: FileDict<FundsData>,
    options: { category: string; department: string },
) => {
    const data = options.category === 'All' && options.department === 'All'
        ? ordinanceData
        : options.category !== 'All'
        ? ordinanceData.filter((row) => deptsDict[row.deptNum].category === options.category)
        : ordinanceData.filter((row) => row.deptNum === options.department);

    const graphNode = createAnnualTrendsGraph(data, fundsDict);
    if (!graphNode) return;

    annualTrends.replaceChildren(graphNode);
};

export { graphAnnualTrends };
